/**
 * Typed endpoint functions for `/api/blocks/*` (docs/APP_PLAN.md §8.1,
 * §12 M4; APP_SPEC §15). Milestone 4a implements only the bare block
 * record (insert/delete/list); the transactional forfeit cascade lands in
 * Milestone 6 without changing this client's surface.
 */
import type { HttpClient } from './http-client.js';
import { buildQueryString } from './query.js';
import type { BlockedUser, PaginationParams } from './types.js';

interface BlockedListEnvelope {
  blocked: BlockedUser[];
}

export interface BlocksApi {
  /** `POST /api/blocks`. Idempotent — blocking an already-blocked user is still a success. */
  block(targetUsername: string): Promise<void>;
  /** `DELETE /api/blocks/:userId`. Idempotent — unblocking a non-blocked user is still a success. */
  unblock(userId: string): Promise<void>;
  /** `GET /api/blocks`. */
  list(pagination?: PaginationParams): Promise<BlockedUser[]>;
}

export function createBlocksApi(http: HttpClient): BlocksApi {
  return {
    async block(targetUsername) {
      await http.requestMaybeEmpty<never>({
        method: 'POST',
        path: '/blocks',
        body: { targetUsername },
      });
    },

    async unblock(userId) {
      await http.requestMaybeEmpty<never>({
        method: 'DELETE',
        path: `/blocks/${encodeURIComponent(userId)}`,
      });
    },

    async list(pagination = {}) {
      const { blocked } = await http.request<BlockedListEnvelope>({
        method: 'GET',
        path: `/blocks${buildQueryString({ ...pagination })}`,
      });
      return blocked;
    },
  };
}
